import type { VendorSnapshot } from './api'

/**
 * Snapshots are static JSON published by TripzOperator, and the dates inside
 * age while the file sits on the CDN. Every template reads `trip.nextDate` as
 * "the next departure", so once that date passes the storefront advertises a
 * departure that already ran, and the booking modal pre-fills it (BUG-011 —
 * Viaara Travels, 2026-10-13). Normalise once at the fetch boundary so no
 * template has to remember.
 */
type SnapshotTrip = VendorSnapshot['trips'][number]

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

/** The viewer's calendar day as YYYY-MM-DD (local time — mini-site visitors are in India). */
export function localCalendarDay(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** The YYYY-MM-DD values in `days` that are today or later — sorted, deduped, junk dropped. */
export function upcomingDays(days: unknown, today: string): string[] {
  if (!Array.isArray(days)) return []
  const upcoming = new Set<string>()
  for (const value of days) {
    const day = typeof value === 'string' ? value.slice(0, 10) : ''
    if (DAY_RE.test(day) && day >= today) upcoming.add(day)
  }
  return Array.from(upcoming).sort()
}

/**
 * A copy of `trip` whose `nextDates` holds only dates that can still be sold
 * and whose `nextDate` is the first of them. Trips that carry no date list are
 * returned as-is: on-request trips have no date by contract, and the legacy
 * "repeat every N days from nextDate" shape keeps a past anchor by design.
 */
export function withUpcomingDatesOnly(trip: SnapshotTrip, today: string = localCalendarDay()): SnapshotTrip {
  if (trip.scheduleType === 'on-request') return trip
  if (trip.repeatEveryDays) return trip
  const everyday = trip.scheduleType === 'everyday' || trip.everyDay === true
  if (!Array.isArray(trip.nextDates) && !everyday) return trip
  const nextDates = upcomingDays(trip.nextDates, today)
  const nextDate = nextDates[0] ?? (everyday ? today : '')
  return { ...trip, nextDates, nextDate }
}

export function normalizeSnapshotDates(snapshot: VendorSnapshot, today: string = localCalendarDay()): VendorSnapshot {
  if (!Array.isArray(snapshot.trips)) return snapshot
  return { ...snapshot, trips: snapshot.trips.map((trip) => withUpcomingDatesOnly(trip, today)) }
}
